import { store } from '@/store';
import type {
    BankTransaction,
    BankTransactionExpandOpts,
    EstateExpandOpts,
    GetBankTransactionParams,
    IBankAccount,
    IDocument,
    IEstate,
    IFetchEstate,
    IFindDocumentsReq,
    IGetRenovationProjectsParams,
} from '@condo/domain';
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query';
import { type MaybeRef, computed, isRef, unref } from 'vue';

import { fetchExternallyRenovatedEstates, fetchOneEstate, findDocuments, getEstateContractors } from '@/api/basecamp.api';
import { fetchBankAccountBalance, getBankTransactions } from '@/api/finance.api';
import { getMfhEstates, getMfhExternalUnits } from '@/api/mfh';
import { fetchEstateCredentials } from '@/api/property-management.api';
export interface IGetEstateFinancialStatusResult {
    bankAccount?: IBankAccount;
    balance?: number;
    percentageOfSecurityHeld: number;
    rentBalance?: boolean;
}

export const useEstateBankTransactions = (
    query: MaybeRef<GetBankTransactionParams>,
    { size, page, order, orderBy }: { size: number; page: number; order: 'DESC' | 'ASC'; orderBy: string } = {
        size: 10,
        page: 1,
        order: 'DESC',
        orderBy: 'valueDate',
    },
    expandOpts?: BankTransactionExpandOpts,
) => {
    const queryResult = useInfiniteQuery(
        ['estate/bank-transactions', query],
        async ({
            pageParam = {
                page,
                size,
            },
        }): Promise<{ bankTransactions: BankTransaction[]; counter: number }> => {
            const queryParams = isRef(query) ? unref(query) : query;
            return getBankTransactions({ ...queryParams }, expandOpts, {
                page: pageParam.page,
                size: pageParam.size,
                order,
                orderBy,
            });
        },
        {
            getNextPageParam: (lastPage, allPages) => {
                const pages = allPages.length;
                const hasMore = lastPage.counter ? pages * size < lastPage.counter : false;
                return hasMore ? { page: pages + 1, size } : undefined;
            },
            getPreviousPageParam: (_, allPages) => (allPages.length > 1 ? { page: allPages.length - 1, size } : undefined),
        },
    );

    return queryResult;
};

export const useBankAccountBalance = (bankAccountIdOrRef: MaybeRef<number | undefined>) => {
    const bankAccountId = computed(() => (isRef(bankAccountIdOrRef) ? bankAccountIdOrRef.value : bankAccountIdOrRef));
    const enabled = computed(() => !!bankAccountId.value);

    return useQuery<{ balance: number; balanceDate: Date } | null>(
        ['bank-account', bankAccountId.value, 'balance'],
        async () => fetchBankAccountBalance(bankAccountId.value!),
        {
            enabled,
            initialData: null,
        },
    );
};

export const useEstateDocuments = (req: IFindDocumentsReq) => {
    const query = useQuery(['estate', 'documents', req], async (): Promise<IDocument[]> => findDocuments(req), {
        initialData: [],
    });

    return { ...query };
};

export const useExternallyRenovatedEstates = (
    queryOrRef?: MaybeRef<Partial<IGetRenovationProjectsParams['query'] & IGetRenovationProjectsParams['relationOpts']>>,
) => {
    const query = computed(() => unref(queryOrRef));
    return useQuery(['renovation-estates/external', query], () => fetchExternallyRenovatedEstates(query.value), {
        initialData: { renovationProjects: [], count: 0 },
    });
};

export const useCurrentEstate = () => computed<IEstate | undefined>(() => store.getters.oneEstate);

export const useEstateContractors = (estateId: number) => {
    return useQuery(['estate', estateId, 'contractors'], async () => getEstateContractors(estateId), {
        initialData: [],
    });
};

export const useMfhEstates = (mfhId: number, expandOptions: EstateExpandOpts) => {
    return useQuery(['mfh', mfhId, 'estates', expandOptions], async () => getMfhEstates(mfhId, expandOptions), {
        initialData: [],
    });
};

export const useEstateCredentials = (estateIdOrRef: MaybeRef<number>) => {
    const estateId = computed(() => unref(estateIdOrRef));

    return useQuery(['estate', estateId, 'credentials'], async () => fetchEstateCredentials(estateId.value), {
        initialData: [],
    });
};

export const useMfhExternalUnits = (mfhId: number, expandOptions: EstateExpandOpts) => {
    return useQuery(['mfh', mfhId, 'estates', expandOptions], async () => getMfhExternalUnits(mfhId), {
        initialData: [],
    });
};

export const useEstate = (estateId: MaybeRef<number | undefined>, params?: MaybeRef<IFetchEstate>) => {
    const enabled = computed(() => !!unref(estateId));
    return useQuery(['estate', estateId, params], async () => fetchOneEstate(unref(estateId), unref(params)), {
        initialData: null,
        enabled,
    });
};
